/**
 * Admin Threading Log — audit surface for the §8 inbound-email match
 * decision. One row per processed email: subject, from address, which
 * matcher fired (references / aes / bare_number / conversation_id / none),
 * parsed ticket number, and the resolution (appended / created /
 * created_linked). Backed by GET /api/admin/threading-matches.
 *
 * Read it for a fortnight after any threading change to verify
 * behaviour in production without tailing logs.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, Funnel, LinkSimple, Plus, ArrowRight } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const MATCHER_META = {
  references:      { label: "References",      color: "text-emerald-800 bg-emerald-50 border-emerald-200" },
  aes:             { label: "[AES-N] tag",     color: "text-emerald-800 bg-emerald-50 border-emerald-200" },
  bare_number:     { label: "Bare Ticket #N",  color: "text-sky-800 bg-sky-50 border-sky-200" },
  conversation_id: { label: "ConversationId",  color: "text-indigo-800 bg-indigo-50 border-indigo-200" },
  none:            { label: "No match",        color: "text-slate-700 bg-slate-100 border-slate-200" },
};
const ACTION_META = {
  appended:       { label: "Appended",       color: "text-emerald-800 bg-emerald-50 border-emerald-200", icon: LinkSimple },
  created:        { label: "Created new",    color: "text-slate-800 bg-slate-100 border-slate-200",      icon: Plus },
  created_linked: { label: "Created + linked", color: "text-amber-900 bg-amber-50 border-amber-200",     icon: ArrowRight },
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

export default function ThreadingLog() {
  const [matcher, setMatcher] = useState("all");
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { limit: 300 };
    if (matcher && matcher !== "all") params.matcher = matcher;
    if (action && action !== "all") params.action = action;
    if (q.trim()) params.q = q.trim();
    api.get("/admin/threading-matches", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load threading log"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matcher, action, q]);

  const entries = data?.entries || [];
  const c1h = data?.counts_last_hour || { by_matcher: {}, by_action: {}, total: 0 };
  const c24h = data?.counts_last_24h || { by_matcher: {}, by_action: {}, total: 0 };

  const nearMisses = useMemo(
    () => entries.filter((e) =>
      (e.matcher === "aes" || e.matcher === "bare_number")
      && !e.resolved_ticket_id
    ).length,
    [entries]
  );

  return (
    <div className="max-w-6xl mx-auto py-8 px-6 space-y-6" data-testid="threading-log-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold text-[#263E4F]">Threading log</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            One row per inbound email: which matcher fired, what number it parsed, and whether we appended to an existing ticket or created a new one. Use this to verify §8 threading behaviour in production without tailing logs.
          </p>
        </div>
        <Button
          variant="outline" onClick={load} disabled={loading}
          data-testid="threading-log-refresh"
        >
          <ArrowClockwise size={16} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Last hour" value={c1h.total} sub="emails processed" testId="threading-count-1h" />
        <SummaryCard label="Last 24 hours" value={c24h.total} sub="emails processed" testId="threading-count-24h" />
        <SummaryCard
          label="Appended (24h)"
          value={c24h.by_action?.appended || 0}
          sub="matched an existing ticket"
          testId="threading-count-appended"
        />
        <SummaryCard
          label="Near-misses"
          value={nearMisses}
          sub="subject #N with no live ticket"
          testId="threading-count-nearmiss"
          amber={nearMisses > 0}
        />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[160px]">
          <Label className="text-xs font-medium text-slate-700"><Funnel size={12} className="inline mr-1" /> Matcher</Label>
          <Select value={matcher} onValueChange={setMatcher}>
            <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="threading-matcher-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All matchers</SelectItem>
              <SelectItem value="references">References header</SelectItem>
              <SelectItem value="aes">[AES-N] tag</SelectItem>
              <SelectItem value="bare_number">Bare Ticket #N</SelectItem>
              <SelectItem value="conversation_id">ConversationId</SelectItem>
              <SelectItem value="none">No match</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs font-medium text-slate-700">Action</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="threading-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="appended">Appended</SelectItem>
              <SelectItem value="created">Created new</SelectItem>
              <SelectItem value="created_linked">Created + linked</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs font-medium text-slate-700">Search subject or from</Label>
          <Input
            className="mt-1 bg-white border-slate-200"
            placeholder="e.g. Ticket #443, or jane@acme.co"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="threading-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Received</th>
              <th className="text-left px-4 py-2 font-medium">From</th>
              <th className="text-left px-4 py-2 font-medium">Subject</th>
              <th className="text-left px-4 py-2 font-medium">Matcher</th>
              <th className="text-left px-4 py-2 font-medium">Parsed #</th>
              <th className="text-left px-4 py-2 font-medium">Resolved</th>
              <th className="text-left px-4 py-2 font-medium">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No entries match those filters.</td></tr>
            )}
            {!loading && entries.map((e) => {
              const mm = MATCHER_META[e.matcher] || MATCHER_META.none;
              const am = ACTION_META[e.action] || { label: e.action || "?", color: "text-slate-700 bg-slate-100 border-slate-200" };
              const ActionIcon = am.icon;
              const nearMiss = (e.matcher === "aes" || e.matcher === "bare_number") && !e.resolved_ticket_id;
              return (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`threading-row-${e.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-600 font-mono text-xs">{fmtTime(e.received_at)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-700">{e.from_email}</td>
                  <td className="px-4 py-2 max-w-[240px] min-w-[160px] truncate" title={e.subject}>{e.subject}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${mm.color}`}>{mm.label}</span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap font-mono text-xs">{e.parsed_number ?? "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {e.resolved_ticket_number ? (
                      <a href={`/tickets/${e.resolved_ticket_id}`} className="text-[#39A36E] hover:underline">
                        #{e.resolved_ticket_number}
                        {e.resolved_status && (
                          <span className="text-slate-500 font-normal ml-1">({e.resolved_status})</span>
                        )}
                      </a>
                    ) : nearMiss ? (
                      <span className="text-amber-800 text-xs" title="Matcher fired but no live ticket exists for that number">near-miss</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${am.color}`}>
                      {ActionIcon && <ActionIcon size={12} />}
                      {am.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Near-miss = a subject-line matcher fired (AES tag or bare Ticket #N) but the referenced ticket doesn't exist as a live record. Usually a renumbered ticket, hard-deleted record, or wrong mailbox — worth eyeballing.
      </p>
    </div>
  );
}


function SummaryCard({ label, value, sub, testId, amber }) {
  const border = amber ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white";
  const valueColor = amber ? "text-amber-900" : "text-[#263E4F]";
  return (
    <div className={`border rounded-lg p-4 ${border}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
